import React, { useState } from 'react';
import {
  FileText,
  Paintbrush,
  Layout,
  Sliders,
  Layers,
  Wand2,
  X,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { ContentTab } from './properties/ContentTab';
import { DesignTab } from './properties/DesignTab';
import { LayoutTab } from './properties/LayoutTab';
import { ResponsiveInspector } from './properties/ResponsiveInspector';
import { AdvancedTab } from './properties/AdvancedTab';
import { AnimationTab } from './properties/AnimationTab';
import { GlobalSettingsInspector } from './properties/GlobalSettingsInspector';
import { AICopywriterModal } from '../ai/AICopywriterModal';
import { AnimationConfig } from '../../types';

type EditorTab = 'content' | 'design' | 'animation' | 'layout' | 'responsive' | 'advanced';

export const PropertiesPanel: React.FC = () => {
  const {
    activeSite,
    activePageId,
    selectedSectionId,
    setSelectedSectionId,
    updateSectionInActivePage,
    deleteSectionInActivePage,
    duplicateSectionInActivePage,
    moveSectionInActivePage,
  } = useSite();

  const [activeTab, setActiveTab] = useState<EditorTab>('content');
  const [aiModalConfig, setAiModalConfig] = useState<{
    isOpen: boolean;
    initialText: string;
    contextTitle: string;
    onApply: (newText: string) => void;
  }>({
    isOpen: false,
    initialText: '',
    contextTitle: '',
    onApply: () => {},
  });

  if (!activeSite) return null;

  const activePage = activeSite.pages?.find((p) => p && p.id === activePageId) || activeSite.pages?.[0];
  const sections = activePage?.sections || [];
  const selectedSection = sections.find((s) => s.id === selectedSectionId);
  const selectedIndex = sections.findIndex((s) => s.id === selectedSectionId);

  // If no section selected, render Global Settings Inspector
  if (!selectedSection) {
    return (
      <aside className="w-80 lg:w-[350px] bg-[#0B1324] border-l border-[#263750] flex flex-col h-full overflow-y-auto text-[#F8FAFC] select-none shrink-0 shadow-xl font-sans custom-scrollbar">
        <GlobalSettingsInspector />
      </aside>
    );
  }

  // Section Type readable labels
  const sectionTypeLabels: Record<string, string> = {
    pricing: 'Fiyatlandırma & Paketler',
    PRICING: 'Fiyatlandırma & Paketler',
    hero: 'Giriş & Manşet (Hero)',
    HERO: 'Giriş & Manşet (Hero)',
    services: 'Hizmetler & Özellikler',
    SERVICES: 'Hizmetler & Özellikler',
    about: 'Hakkımızda & Hikaye',
    ABOUT: 'Hakkımızda & Hikaye',
    contact: 'İletişim & Form',
    CONTACT: 'İletişim & Form',
    yacht_fleet: 'Yat & Tekne Filosu',
    YACHT_FLEET: 'Yat & Tekne Filosu',
    faq: 'Sıkça Sorulan Sorular',
    FAQ: 'Sıkça Sorulan Sorular',
    testimonials: 'Müşteri Yorumları',
    TESTIMONIALS: 'Müşteri Yorumları',
    gallery: 'Fotoğraf Galerisi',
    GALLERY: 'Fotoğraf Galerisi',
    stats: 'İstatistik & Rakamlar',
    STATS: 'İstatistik & Rakamlar',
    team: 'Ekip & Kadro',
    TEAM: 'Ekip & Kadro',
    references: 'Referans Logoları',
    REFERENCES: 'Referans Logoları',
    reservation_cta: 'Rezervasyon Çağrısı',
    RESERVATION_CTA: 'Rezervasyon Çağrısı',
    yacht_routes: 'Mavi Tur Rotaları',
    YACHT_ROUTES: 'Mavi Tur Rotaları',
    yacht_marinas: 'Marinalar & Limanlar',
    YACHT_MARINAS: 'Marinalar & Limanlar',
    availability_calendar: 'Müsaitlik Takvimi',
    AVAILABILITY_CALENDAR: 'Müsaitlik Takvimi',
    header: 'Üst Menü (Header)',
    HEADER: 'Üst Menü (Header)',
    footer: 'Alt Bilgi (Footer)',
    FOOTER: 'Alt Bilgi (Footer)',
  };

  const currentLabel = sectionTypeLabels[selectedSection.type] || 'Özel Bölüm';

  const handleUpdateContent = (updates: any) => {
    updateSectionInActivePage(selectedSection.id, {
      ...selectedSection.content,
      ...updates,
    });
  };

  const handleUpdateStyle = (updates: any) => {
    updateSectionInActivePage(
      selectedSection.id,
      selectedSection.content,
      {
        ...selectedSection.style,
        ...updates,
      }
    );
  };

  const handleDuplicate = () => {
    duplicateSectionInActivePage(selectedSection.id);
  };

  const handleDelete = () => {
    deleteSectionInActivePage(selectedSection.id);
    setSelectedSectionId(null);
  };

  const handleMoveUp = () => {
    if (selectedIndex <= 0) return;
    moveSectionInActivePage(selectedIndex, selectedIndex - 1);
  };

  const handleMoveDown = () => {
    if (selectedIndex < 0 || selectedIndex >= sections.length - 1) return;
    moveSectionInActivePage(selectedIndex, selectedIndex + 1);
  };

  const handleUpdateAnimation = (newAnimation: AnimationConfig) => {
    updateSectionInActivePage(
      selectedSection.id,
      selectedSection.content,
      {
        ...selectedSection.style,
        animation: newAnimation,
      }
    );
  };

  const handlePreviewAnimation = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('adiosoft-preview-anim', {
          detail: { sectionId: selectedSection.id },
        })
      );
    }
  };

  const tabs: Array<{ id: EditorTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'content', label: 'İçerik', icon: FileText },
    { id: 'design', label: 'Tasarım', icon: Paintbrush },
    { id: 'animation', label: 'Animasyon', icon: Sparkles },
    { id: 'layout', label: 'Düzen', icon: Layout },
    { id: 'responsive', label: 'Mobil', icon: Smartphone },
    { id: 'advanced', label: 'Gelişmiş', icon: Sliders },
  ];

  return (
    <aside className="w-80 lg:w-[350px] bg-[#0B1324] border-l border-[#263750] flex flex-col h-full overflow-hidden text-[#F8FAFC] select-none shrink-0 shadow-xl z-20 font-sans">
      {/* Top Header Bar with active section context */}
      <div className="p-3.5 bg-[#111B2E] border-b border-[#263750] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-xl bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30 flex items-center justify-center shrink-0">
            <Layers className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-blue-400 block leading-tight">
              Seçili Bölüm
            </span>
            <h3 className="text-xs font-bold text-[#F8FAFC] truncate">{currentLabel}</h3>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Quick AI Writing Assistant Trigger */}
          <button
            type="button"
            title="Yapay Zeka ile Metinleri Yeniden Yaz"
            onClick={() =>
              setAiModalConfig({
                isOpen: true,
                initialText: selectedSection.content?.title || selectedSection.content?.description || '',
                contextTitle: `${currentLabel} Metinleri`,
                onApply: (newText) => {
                  handleUpdateContent({
                    title: newText,
                  });
                },
              })
            }
            className="px-2 py-1 rounded-lg bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 border border-indigo-500/30 transition flex items-center gap-1 text-[11px] font-bold"
          >
            <Wand2 className="w-3.5 h-3.5 text-indigo-400" />
            <span className="hidden sm:inline">AI Yazıcı</span>
          </button>

          {/* Close / Deselect Section */}
          <button
            type="button"
            onClick={() => setSelectedSectionId(null)}
            title="Seçimi Kaldır"
            className="p-1.5 rounded-lg text-[#94A3B8] hover:text-white hover:bg-[#162238] transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 6 Professional Category Tabs */}
      <div className="grid grid-cols-6 bg-[#111B2E]/60 border-b border-[#263750] p-1.5 gap-1 shrink-0">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 rounded-xl text-[10px] font-bold flex flex-col items-center justify-center gap-1 transition ${
                isActive
                  ? 'bg-[#2563EB] text-white shadow-md'
                  : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#162238]'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="leading-none">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content Body */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#0B1324]">
        {activeTab === 'content' && (
          <ContentTab section={selectedSection} onUpdateContent={handleUpdateContent} />
        )}

        {activeTab === 'design' && (
          <DesignTab section={selectedSection} onUpdateStyle={handleUpdateStyle} />
        )}

        {activeTab === 'animation' && (
          <AnimationTab
            section={selectedSection}
            allPageSections={sections}
            onUpdateAnimation={handleUpdateAnimation}
            onPreviewAnimation={handlePreviewAnimation}
          />
        )}

        {activeTab === 'layout' && (
          <LayoutTab section={selectedSection} onUpdateStyle={handleUpdateStyle} />
        )}

        {activeTab === 'responsive' && (
          <ResponsiveInspector section={selectedSection} onUpdateStyle={handleUpdateStyle} />
        )}

        {activeTab === 'advanced' && (
          <AdvancedTab
            section={selectedSection}
            onUpdateStyle={handleUpdateStyle}
            onDuplicateSection={handleDuplicate}
            onDeleteSection={handleDelete}
            onMoveUp={selectedIndex > 0 ? handleMoveUp : undefined}
            onMoveDown={selectedIndex < sections.length - 1 ? handleMoveDown : undefined}
          />
        )}
      </div>

      {/* AI Copywriter Modal */}
      {aiModalConfig.isOpen && (
        <AICopywriterModal
          initialText={aiModalConfig.initialText}
          contextTitle={aiModalConfig.contextTitle}
          onClose={() => setAiModalConfig((prev) => ({ ...prev, isOpen: false }))}
          onApply={aiModalConfig.onApply}
        />
      )}
    </aside>
  );
};
